import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import JsonView from '@uiw/react-json-view';
import { Settings, X, Copy, Trash2, Moon, Sun, Sidebar, Power, Link as LinkIcon, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useThemeStore } from '../stores/theme';
import { useUIStore } from '../stores/ui';

export default function DevTools() {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  
  const authState = useAuthStore();
  const themeState = useThemeStore();
  const uiState = useUIStore();

  const [expandedSections, setExpandedSections] = useState({
    auth: false,
    theme: false,
    ui: false
  });

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (process.env.NODE_ENV !== 'development') return null;

  const hasToken = !!localStorage.getItem('auth_token');

  const copyToken = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      navigator.clipboard.writeText(token);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const clearStorage = () => {
    if (window.confirm('Clear all localStorage and reload?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const ExpandableSection = ({ title, section, state }) => (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden mb-2">
      <button 
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-sm font-medium"
      >
        <span className="text-slate-800 dark:text-slate-200">{title}</span>
        {expandedSections[section] ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
      </button>
      {expandedSections[section] && (
        <div className="p-2 bg-white dark:bg-slate-900 overflow-x-auto text-xs">
          <JsonView 
            value={state} 
            displayDataTypes={false} 
            displayObjectSize={false}
            style={{ 
              backgroundColor: 'transparent',
              '--w-rjv-color': 'var(--w-rjv-color, currentColor)',
              '--w-rjv-key-string': themeState.dark ? '#9cdcfe' : '#0451a5',
              '--w-rjv-type-string-color': themeState.dark ? '#ce9178' : '#a31515',
              '--w-rjv-type-boolean-color': themeState.dark ? '#569cd6' : '#0000ff',
            }} 
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-50 p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors flex items-center justify-center group"
        title="Open DevTools"
      >
        <Settings size={20} className="group-hover:rotate-90 transition-transform duration-300" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/40 z-[9998] backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 md:left-4 md:right-auto md:bottom-20 md:w-96 max-h-[85vh] bg-white dark:bg-slate-900 shadow-2xl rounded-t-xl md:rounded-xl z-[9999] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center gap-2">
                  <Settings size={18} className="text-indigo-600 dark:text-indigo-400" />
                  <h2 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">Developer Tools</h2>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-md text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                
                {/* Auth State */}
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Auth State</h3>
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-3 border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600 dark:text-slate-400">User Email:</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">{authState.user?.email || 'Not logged in'}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Roles:</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">{authState.user?.roles?.join(', ') || 'None'}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Has Token:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${hasToken ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {hasToken ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                      <button 
                        onClick={copyToken}
                        disabled={!hasToken}
                        className="flex-1 flex items-center justify-center gap-2 py-1.5 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-md text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {copiedToken ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                        {copiedToken ? 'Copied' : 'Copy Token'}
                      </button>
                      <button 
                        onClick={() => { authState.logout(); window.location.reload(); }}
                        className="flex items-center justify-center gap-2 py-1.5 px-3 bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-900/50 rounded-md text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                      >
                        <Power size={14} />
                        Logout
                      </button>
                    </div>
                  </div>
                </section>

                {/* API Info */}
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">API Info</h3>
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-2 border border-slate-200 dark:border-slate-700 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">API URL:</span>
                      <span className="font-mono text-xs text-slate-900 dark:text-slate-100">{process.env.REACT_APP_API_URL || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">Build Mode:</span>
                      <span className="font-mono text-xs text-slate-900 dark:text-slate-100">{process.env.NODE_ENV}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">App Version:</span>
                      <span className="font-mono text-xs text-slate-900 dark:text-slate-100">0.1.0</span>
                    </div>
                  </div>
                </section>

                {/* Store Inspector */}
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Store Inspector</h3>
                  <ExpandableSection title="useAuthStore" section="auth" state={{ isAuthenticated: authState.isAuthenticated, user: authState.user }} />
                  <ExpandableSection title="useThemeStore" section="theme" state={{ mode: themeState.mode, dark: themeState.dark }} />
                  <ExpandableSection title="useUIStore" section="ui" state={{ sidebarOpen: uiState.sidebarOpen, breadcrumbs: uiState.breadcrumbs }} />
                </section>

                {/* Quick Actions */}
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => themeState.setMode(themeState.dark ? 'light' : 'dark')}
                      className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {themeState.dark ? <Sun size={14} /> : <Moon size={14} />}
                      Toggle Theme
                    </button>
                    <button
                      onClick={uiState.toggleSidebar}
                      className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <Sidebar size={14} />
                      Toggle Sidebar
                    </button>
                    <button
                      onClick={copyUrl}
                      className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <LinkIcon size={14} />
                      Copy URL
                    </button>
                    <button
                      onClick={clearStorage}
                      className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 size={14} />
                      Clear Storage
                    </button>
                  </div>
                </section>

              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
